import { sectionRenderer } from '@theme/section-renderer';

const instanceKey = Symbol.for('goriscorp.collectionFacets');
window[instanceKey]?.destroy();

const searchSelector = '.goris-extension-search input[type="search"]';
const contextSelector = 'script[data-goris-filter-context]';
const controllers = new Map();
const lifecycle = new AbortController();

function readContext(root, sectionId) {
  for (const script of root.querySelectorAll(contextSelector)) {
    try {
      const context = JSON.parse(script.textContent);
      if (
        typeof context.sectionId === 'string' &&
        (!sectionId || context.sectionId === sectionId) &&
        typeof context.unfiltered === 'boolean' &&
        Array.isArray(context.groups)
      ) return context;
    } catch {}
  }
  return null;
}

function baselineURL() {
  const url = new URL(window.location.href);
  for (const key of Array.from(url.searchParams.keys())) {
    if (key.startsWith('filter.') || ['page', 'sort_by', 'section_id'].includes(key)) {
      url.searchParams.delete(key);
    }
  }
  url.hash = '';
  url.searchParams.sort();
  return url;
}

function normalize(value) {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase()
    .replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .replace(/[\p{Pd}\u2212]/gu, '-')
    .replace(/\b(op)\s*-?\s*(\d+)\b/g, '$1$2');
}

class CollectionFacets {
  constructor(root, context) {
    this.root = root;
    this.sectionId = context.sectionId;
    this.baselines = new Map();
    this.requests = new Map();
    this.searches = new Map();
    this.scrollPositions = new Map();
    this.listener = new AbortController();
    this.observer = new MutationObserver(() => this.schedule(true));
    this.frame = null;
    this.restoreScroll = false;
    this.focusedSearch = null;
    this.disposed = false;
    const options = { signal: this.listener.signal, capture: true };

    root.addEventListener('input', (event) => this.onSearch(event), options);
    root.addEventListener('search', (event) => this.onSearch(event), options);
    root.addEventListener('change', (event) => {
      if (event.target.matches(searchSelector)) event.stopPropagation();
    }, options);
    root.addEventListener('keydown', (event) => {
      if (event.target.matches(searchSelector) && event.key === 'Enter') event.preventDefault();
    }, options);
    root.addEventListener('scroll', (event) => this.captureScroll(event.target), options);
    root.addEventListener('focusout', () => this.schedule(), options);
    document.addEventListener('focusin', (event) => {
      const input = event.target;
      this.focusedSearch = root.contains(input) && input.matches(searchSelector)
        ? { input, key: this.searchKey(input), start: input.selectionStart, end: input.selectionEnd }
        : null;
    }, options);
    window.addEventListener('online', () => {
      if (this.disposed || !this.root.isConnected) return;
      const url = baselineURL();
      const request = this.requests.get(url.href);
      if (!request || request.pending || request.timer !== null || request.attempts < 3 || this.baselines.has(url.href)) return;
      this.requests.delete(url.href);
      this.loadBaseline(url, false);
    }, { signal: this.listener.signal });

    this.refresh();
    this.observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'disabled', 'value', 'data-filter-param', 'data-goris-value'],
    });
  }

  destroy() {
    this.disposed = true;
    this.listener.abort();
    this.observer.disconnect();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    for (const request of this.requests.values()) clearTimeout(request.timer);
    this.requests.clear();
  }

  schedule(restoreScroll = false) {
    if (this.disposed) return;
    this.restoreScroll ||= restoreScroll;
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.root.isConnected) return;
      this.refresh();
    });
  }

  setBaseline(key, context) {
    this.baselines.set(key, new Map(context.groups
      .filter((group) => typeof group.param === 'string' && typeof group.useful === 'boolean')
      .map((group) => [group.param, group.useful])));
  }

  loadBaseline(url, useCache = true) {
    const key = url.href;
    if (this.disposed || this.baselines.has(key)) return;
    let request = this.requests.get(key);
    if (!request) {
      request = { attempts: 0, pending: false, timer: null };
      this.requests.set(key, request);
    }
    if (request.pending || request.timer !== null || request.attempts >= 3) return;
    request.attempts += 1;
    request.pending = true;
    // A retry must bypass cached error markup; the renderer also mutates its URL.
    sectionRenderer.getSectionHTML(this.sectionId, useCache && request.attempts === 1, new URL(key), this.listener.signal).then((html) => {
      if (this.disposed) return;
      const context = readContext(new DOMParser().parseFromString(html, 'text/html'), this.sectionId);
      if (context?.unfiltered && !this.baselines.has(key)) {
        this.setBaseline(key, context);
        this.schedule();
      }
    }).catch(() => {}).finally(() => {
      request.pending = false;
      if (this.disposed) return;
      if (this.baselines.has(key)) {
        this.requests.delete(key);
      } else if (request.attempts < 3) {
        request.timer = setTimeout(() => {
          request.timer = null;
          this.loadBaseline(new URL(key));
        }, request.attempts === 1 ? 1000 : 3000);
      }
    });
  }

  groupActive(group, parameters) {
    if (group.querySelector('input:checked')) return true;
    const param = group.dataset.filterParam;
    const price = group.dataset.filterType === 'price_range';
    if (price && Array.from(group.querySelectorAll('input[name]')).some((input) => input.value !== '')) return true;
    return Array.from(parameters).some(([name, value]) => value !== '' && (
      name === param || (price && (name === `${param}.gte` || name === `${param}.lte`))
    ));
  }

  searchKey(input) {
    return input.dataset.listId || input.closest('.goris-extension-search')?.dataset.listId || input.id;
  }

  searchList(input) {
    const list = document.getElementById(this.searchKey(input));
    return list && input.closest('.goris-facet-group')?.contains(list) ? list : null;
  }

  onSearch(event) {
    const input = event.target;
    if (!input.matches(searchSelector)) return;
    event.stopPropagation();
    this.searches.set(this.searchKey(input), input.value);
    this.focusedSearch = { input, key: this.searchKey(input), start: input.selectionStart, end: input.selectionEnd };
    this.applySearch(input);
    this.captureScroll(this.searchList(input));
  }

  applySearch(input) {
    const key = this.searchKey(input);
    if (!this.searches.has(key)) this.searches.set(key, input.value);
    const query = this.searches.get(key);
    if (input.value !== query) input.value = query;
    const list = this.searchList(input);
    if (!list) return;
    const tokens = normalize(query).trim().split(/\s+/).filter(Boolean);
    let matches = 0;
    for (const item of list.querySelectorAll('[data-goris-value]')) {
      const label = normalize(item.dataset.gorisValue || '');
      const matched = tokens.every((token) => label.includes(token));
      item.hidden = !matched && !item.contains(document.activeElement);
      if (matched) matches += 1;
    }
    const empty = input.closest('.goris-facet-group')?.querySelector('.goris-extension-empty');
    if (empty) empty.hidden = matches !== 0;
  }

  scrollTargets() {
    const targets = new Map();
    for (const input of this.root.querySelectorAll(searchSelector)) {
      const key = this.searchKey(input);
      const list = this.searchList(input);
      if (!list) continue;
      targets.set(`${key}:list`, list);
      const panel = input.closest('.goris-facet-content, .facets__panel-content');
      if (panel) targets.set(`${key}:panel`, panel);
    }
    for (const drawer of this.root.querySelectorAll('.facets-drawer__filters')) {
      const form = drawer.closest('form');
      if (form?.id) targets.set(`${form.id}:drawer`, drawer);
    }
    return targets;
  }

  captureScroll(target) {
    if (!target) return;
    for (const [key, element] of this.scrollTargets()) {
      if (element === target) this.scrollPositions.set(key, { top: element.scrollTop, left: element.scrollLeft });
    }
  }

  refresh() {
    const context = readContext(this.root, this.sectionId);
    if (!context) return;
    const url = baselineURL();
    const key = url.href;
    if (context.unfiltered) this.setBaseline(key, context);
    else if (!this.baselines.has(key)) this.loadBaseline(url);
    const baseline = this.baselines.get(key);
    const parameters = new URL(window.location.href).searchParams;
    for (const group of this.root.querySelectorAll('.goris-facet-group[data-filter-param]')) {
      const param = group.dataset.filterParam;
      group.hidden = param !== 'filter.v.availability' && baseline?.get(param) === false &&
        !this.groupActive(group, parameters) && !group.contains(document.activeElement);
    }
    for (const input of this.root.querySelectorAll(searchSelector)) this.applySearch(input);
    const focus = this.focusedSearch;
    if (focus && !focus.input.isConnected && document.activeElement === document.body) {
      const replacement = Array.from(this.root.querySelectorAll(searchSelector))
        .find((input) => this.searchKey(input) === focus.key && input.getClientRects().length > 0);
      if (replacement) {
        replacement.focus({ preventScroll: true });
        if (focus.start !== null && focus.end !== null) replacement.setSelectionRange(focus.start, focus.end);
      }
    }
    for (const [scrollKey, target] of this.scrollTargets()) {
      const position = this.scrollPositions.get(scrollKey);
      if (this.restoreScroll && position) {
        target.scrollTop = position.top;
        target.scrollLeft = position.left;
      } else if (!position) {
        this.scrollPositions.set(scrollKey, { top: target.scrollTop, left: target.scrollLeft });
      }
    }
    this.restoreScroll = false;
  }
}

function registerSections() {
  for (const script of document.querySelectorAll(contextSelector)) {
    const root = script.closest('.shopify-section');
    if (!root) continue;
    const context = readContext(root);
    if (!context) continue;
    const current = controllers.get(context.sectionId);
    if (current?.root === root) continue;
    current?.destroy();
    controllers.set(context.sectionId, new CollectionFacets(root, context));
  }
  for (const [sectionId, controller] of controllers) {
    if (!controller.root.isConnected) {
      controller.destroy();
      controllers.delete(sectionId);
    }
  }
}

registerSections();
document.addEventListener('shopify:section:load', registerSections, { signal: lifecycle.signal });
document.addEventListener('shopify:section:unload', registerSections, { signal: lifecycle.signal });
window[instanceKey] = {
  destroy() {
    lifecycle.abort();
    for (const controller of controllers.values()) controller.destroy();
    controllers.clear();
  },
};
